<template>
  <div class="author-select">
    <label class="author-select__label">
      {{ label }}
    </label>
    <div class="author-select__container" @click="showInput = true">
      <!-- Selected Author Chip -->
      <div v-if="selectedAuthor" class="author-select__selected">
        <avatar
          class="author-select__selected-avatar"
          :size="28"
          :src="selectedAuthor.avatar_url"
          :username="selectedAuthor.name"
        />
        <span class="author-select__selected-name">{{ selectedAuthor.name }}</span>
        <button type="button" class="author-select__clear-btn" @click.stop="clearAuthor()">
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
        </button>
      </div>

      <!-- Search Input -->
      <input
        v-if="showInput || !selectedAuthor"
        id="select-input"
        :placeholder="selectedAuthor ? '' : $t('Search users...')"
        v-model="value"
        type="text"
        name="select-input"
        class="author-select__input"
        autofocus
        autocomplete="off"
        :required="!selectedAuthor"
        @input="$emit('input', $event.target.value)"
        @keydown="resetSelectedAuthor"
      />
    </div>

    <!-- Dropdown -->
    <div
      class="author-select__dropdown"
      :class="{ 'author-select__dropdown--board': boardPostAuthorSelect }"
      v-if="showResultDropdown"
      v-click-outside="onClickOutside"
    >
      <div class="author-select__dropdown-scroll">
        <!-- User Options -->
        <div
          class="author-select__option"
          v-for="option in filteredOptions"
          :key="option.id"
          @click="selectAuthor(option)"
        >
          <avatar
            class="author-select__option-avatar"
            :size="32"
            :src="option.avatar_url"
            :username="option.name"
          />
          <div class="author-select__option-details">
            <span class="author-select__option-name">{{ option.name }}</span>
            <span class="author-select__option-email">{{ option.email }}</span>
          </div>
        </div>

        <!-- No Results -->
        <div class="author-select__empty" v-if="filteredOptions.length == 0 && showOptions">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
          </svg>
          <span>{{ $t('No users found') }}</span>
        </div>
      </div>

      <!-- Create New User Button -->
      <button
        v-if="showOptions"
        class="author-select__create-btn"
        type="button"
        @click="showField()"
      >
        <svg viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd" />
        </svg>
        {{ $t('Create new user') }}
      </button>
    </div>
  </div>
</template>

<script>
import clickOutSide from "v-click-outside";

export default {
  name: "custom-select",

  props: ["value", "label", "options", "current-user", "showOptions","board-post-author-select"],

  emits: ["showAuthorEmailField", "addSelectedAuthor", "removeSelectedAuthor"],

  directives: {
    clickOutSide,
  },
  data() {
    return {
      selectedAuthor: this.currentUser,
      showInput: false,
      showResultDropdown: false,
      searchResults: this.options,
    };
  },

  computed: {
    filteredOptions() {
      return this.searchResults;
    },
  },

  methods: {
    resetSelectedAuthor(event) {
      if (event.keyCode == 8) {
        this.selectedAuthor = null;
        this.$emit("removeSelectedAuthor");
      }
    },
    selectAuthor(author) {
      this.selectedAuthor = author;
      this.$emit("addSelectedAuthor", this.selectedAuthor.id);
      this.$emit("hideAuthorEmailField");
      this.value = "";
      this.showResultDropdown = false;
    },
    onClickOutside (event) {
      console.log('Clicked outside. Event: ', event)
      this.showResultDropdown = false;
    },
    showField() {
      this.$emit("showAuthorEmailField", this.value);
      this.$emit("showDropdown", false);
      this.clearAuthor();
    },
    clearAuthor() {
      this.selectedAuthor = null;
      this.$emit("removeSelectedAuthor");
      if (this.showResultDropdown) this.showResultDropdown = false;
    },
  },

  watch: {
    value(val) {
      if (val.length >= 2) {
        var query = new RegExp(val, "i");
        this.searchResults = this.options.filter((user) => {
          return user.name.match(query);
        });
        this.showResultDropdown = true;
      } else if (val.length == 1 && this.selectedAuthor != null) {
        this.clearAuthor();
      } else if (val.length == 0) {
        this.showResultDropdown = false;
        this.$emit("hideAuthorEmailField");
        this.$emit("showDropdown", true);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.author-select {
  position: relative;
}

.author-select__label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #404040;
  margin-bottom: 6px;
}

.author-select__container {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  cursor: text;
  transition: all 0.15s ease;
  min-height: 46px;

  &:hover {
    border-color: #9ca3af;
  }

  &:focus-within {
    border-color: var(--accent-color, #009be5);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent-color, #009be5) 15%, transparent);
  }
}

.author-select__selected {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px 4px 4px;
  background: #f3f4f6;
  border-radius: 8px;
  flex-shrink: 0;
}

.author-select__selected-avatar {
  flex-shrink: 0;
  border-radius: 6px;
  border: 2px solid #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}

.author-select__selected-name {
  font-size: 13px;
  font-weight: 500;
  color: #404040;
  white-space: nowrap;
}

.author-select__clear-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #a3a3a3;
  transition: color 0.15s ease;
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    color: #ef4444;
  }
}

.author-select__input {
  flex: 1;
  width: 100%;
  padding: 4px;
  font-size: 13px;
  border: none;
  background: transparent;
  outline: none;

  &::placeholder {
    color: #a3a3a3;
  }
}

.author-select__dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 50;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.author-select__dropdown--board {
  width: 100%;
}

.author-select__dropdown-scroll {
  max-height: 240px;
  overflow-y: auto;
  overscroll-behavior: contain;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f9fafb;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;

    &:hover {
      background: #9ca3af;
    }
  }
}

.author-select__option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: #f9fafb;
  }

  &:first-child {
    padding-top: 12px;
  }

  &:last-child {
    padding-bottom: 12px;
  }
}

.author-select__option-avatar {
  flex-shrink: 0;
  border-radius: 8px;
  border: 2px solid #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.author-select__option-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.author-select__option-name {
  font-size: 13px;
  font-weight: 500;
  color: #404040;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.author-select__option-email {
  font-size: 12px;
  color: #737373;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.author-select__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  color: #a3a3a3;

  svg {
    width: 32px;
    height: 32px;
    margin-bottom: 8px;
    opacity: 0.5;
  }

  span {
    font-size: 13px;
  }
}

.author-select__create-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 12px 14px;
  border: none;
  border-top: 1px solid #f3f4f6;
  background: #fafafa;
  font-size: 13px;
  font-weight: 500;
  color: var(--accent-color, #009be5);
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background: #f3f4f6;
    color: color-mix(in srgb, var(--accent-color, #009be5) 80%, black);
  }
}

@media screen and (max-width: 640px) {
  .author-select__dropdown {
    left: 0;
    right: 0;
  }
}
</style>